import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Modal from '../../Components/UI/Modal/Modal';
import { formatDate } from '../../Utils/date';

const money = (value) => {
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(number)
    : 'Non renseigné';
};

const STATUS_LABELS = {
  assigned: 'Chantier confirmé',
  finished: 'Travaux terminés',
  paid: 'Paiement confirmé',
  reviewed: 'Projet clôturé',
};

const DevisModalPreview = ({ showModal, handleClick, demand }) => {
  const history = useHistory();
  const opened = showModal === 'open';
  const contract = demand?.contract;
  const estimation = demand?.estimation;

  const close = () => handleClick?.();

  const openDevis = () => {
    if (!demand || !contract) return;
    close();
    history.push(`/devis?demandId=${encodeURIComponent(demand.demand_id)}`, { demandToPrint: demand });
  };

  const openArtisan = () => {
    const artisanId = contract?.artisan_id || estimation?.artisan_id;
    if (!artisanId) return;
    close();
    history.push('/artisanboutique', { artisanId });
  };

  const artisanName = [
    contract?.artisan_firstname || estimation?.artisan_firstname,
    contract?.artisan_lastname || estimation?.artisan_lastname,
  ].filter(Boolean).join(' ') || 'Artisan';

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>Suivi du projet</Eyebrow>
          <Title>{STATUS_LABELS[demand?.demand_status] || 'Projet en cours'}</Title>
        </div>
        <CloseButton type="button" aria-label="Fermer" onClick={close}>×</CloseButton>
      </Header>

      {!demand ? (
        <EmptyState>Ce projet n’est plus disponible.</EmptyState>
      ) : (
        <>
          <People>
            <Person>
              <Avatar src={demand.client_profile_image || '/images/user.svg'} alt="" />
              <div>
                <span>Client</span>
                <strong>{[demand.user_firstname, demand.user_lastname].filter(Boolean).join(' ') || 'Client'}</strong>
              </div>
            </Person>
            <Person as="button" type="button" onClick={openArtisan} $interactive>
              <Avatar src={contract?.artisan_profile_image || estimation?.artisan_profile_image || '/images/user.svg'} alt="" />
              <div>
                <span>Artisan</span>
                <strong>{artisanName}</strong>
              </div>
            </Person>
          </People>

          <Timeline>
            <TimelineItem $active>
              <Dot />
              <div><span>Demande publiée</span><strong>{formatDate(demand.date)}</strong></div>
            </TimelineItem>
            <TimelineItem $active={Boolean(estimation)}>
              <Dot />
              <div><span>Estimation reçue</span><strong>{estimation ? 'Validée' : 'En attente'}</strong></div>
            </TimelineItem>
            <TimelineItem $active={Boolean(contract)}>
              <Dot />
              <div><span>Contrat</span><strong>{contract ? 'Accepté' : 'En préparation'}</strong></div>
            </TimelineItem>
            <TimelineItem $active={['finished', 'paid', 'reviewed'].includes(demand.demand_status)}>
              <Dot />
              <div>
                <span>Fin des travaux</span>
                <strong>{contract?.end_date ? formatDate(contract.end_date) : 'À venir'}</strong>
              </div>
            </TimelineItem>
          </Timeline>

          <Details>
            <Detail $wide>
              <span>Votre demande</span>
              <p>{demand.description || 'Aucune description renseignée.'}</p>
            </Detail>
            <Detail $wide>
              <span>Prestation convenue</span>
              <p>{contract?.description || estimation?.estimation_description || 'Le détail est en cours de préparation.'}</p>
            </Detail>
            <Detail>
              <span>Adresse du chantier</span>
              <strong>{demand.demand_address || 'Non renseignée'}</strong>
            </Detail>
            <Detail>
              <span>Montant du contrat</span>
              <strong>{contract ? money(contract.total_cost) : 'En attente du devis'}</strong>
            </Detail>
          </Details>

          <Actions>
            <SecondaryButton type="button" onClick={close}>Fermer</SecondaryButton>
            <PrimaryButton type="button" onClick={openDevis} disabled={!contract}>
              {contract ? 'Ouvrir le devis / PDF' : 'Devis non disponible'}
            </PrimaryButton>
          </Actions>
        </>
      )}
    </Modal>
  );
};

const Header = styled.header`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.4rem;
`;
const Eyebrow = styled.div`
  margin-bottom: 0.35rem;
  color: #a88a5d;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;
const Title = styled.h2`margin: 0; color: #111111; font-size: clamp(1.35rem, 4vw, 1.9rem);`;
const CloseButton = styled.button`
  width: 2.5rem; height: 2.5rem; flex: 0 0 auto; border: 0; border-radius: 50%;
  background: #f1ece5; color: #111111; font: inherit; font-size: 1.6rem; cursor: pointer;
`;
const People = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const Person = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.9rem;
  border: 1px solid #e5e1da;
  border-radius: 0.9rem;
  background: #faf8f4;
  color: #111111;
  font: inherit;
  text-align: left;
  cursor: ${({ $interactive }) => ($interactive ? 'pointer' : 'default')};
  div { display: grid; gap: 0.1rem; }
  span { color: #687681; font-size: 0.75rem; text-transform: uppercase; }
  &:hover { border-color: ${({ $interactive }) => ($interactive ? '#c0a375' : '#e5e1da')}; }
`;
const Avatar = styled.img`width: 3rem; height: 3rem; border-radius: 50%; object-fit: cover; background: #efede8;`;
const Timeline = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.4rem;
  margin: 1.2rem 0;
  @media (max-width: 650px) { grid-template-columns: 1fr 1fr; }
`;
const TimelineItem = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 0.45rem;
  padding: 0.75rem;
  border-radius: 0.8rem;
  background: ${({ $active }) => ($active ? '#eef7f1' : '#f4f6f7')};
  color: ${({ $active }) => ($active ? '#234f34' : '#77838b')};
  div { display: grid; gap: 0.1rem; }
  span { font-size: 0.72rem; }
  strong { font-size: 0.8rem; }
`;
const Dot = styled.i`
  width: 0.55rem; height: 0.55rem; margin-top: 0.25rem; border-radius: 50%; background: currentColor;
`;
const Details = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.8rem;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const Detail = styled.div`
  grid-column: ${({ $wide }) => ($wide ? '1 / -1' : 'auto')};
  padding: 1rem;
  border-radius: 0.9rem;
  background: #faf8f4;
  color: #111111;
  span { display: block; margin-bottom: 0.4rem; color: #726b61; font-size: 0.76rem; font-weight: 800; text-transform: uppercase; }
  p { margin: 0; white-space: pre-wrap; line-height: 1.55; }
`;
const EmptyState = styled.p`margin: 0; padding: 1rem; border-radius: 0.8rem; background: #faf8f4; color: #66615b;`;
const Actions = styled.div`
  display: flex; justify-content: flex-end; gap: 0.7rem; margin-top: 1.3rem;
  @media (max-width: 520px) { flex-direction: column-reverse; }
`;
const BaseButton = styled.button`
  min-height: 2.8rem; padding: 0.72rem 1.05rem; border-radius: 999px; font: inherit; font-weight: 800; cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const PrimaryButton = styled(BaseButton)`border: 1px solid #c0a375; background: #c0a375; color: #111111;`;
const SecondaryButton = styled(BaseButton)`border: 1px solid #e7dfd2; background: #fff; color: #111111;`;

export default DevisModalPreview;
